import React, { useEffect, useState } from "react";
import Container from "../components/Container";
import BreadCrumb from "../components/BreadCrumb";
import { useDispatch, useSelector } from "react-redux";
import { getOrders, updateAnOrder } from "../features/user/userSlice";
import DataNotFound from "../components/DataNotFound";
import ApiResponseModal from "../components/ApiResponseModal";
import { useNavigate } from "react-router-dom";

const Orders = () => {
  const dispatch = useDispatch();
  const orderState = useSelector(
    (state) => state?.auth?.getorderedProduct?.orders
  );
  const navigate = useNavigate();
  const [apiModal, setApiModal] = useState({
    show: false,
    type: "success",
    title: "",
    message: "",
  });

  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnReason, setReturnReason] = useState("");
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const customer = localStorage.getItem("customer")
    ? JSON.parse(localStorage.getItem("customer"))
    : null;

  const config = {
    headers: {
      Authorization: `Bearer ${customer?.token || ""}`,
      Accept: "application/json",
    },
  };

  useEffect(() => {
    dispatch(getOrders(config));
  }, []);

  const paginatedOrders = orderState
    ? [...orderState]
        .reverse()
        .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    : [];

  const totalPages = orderState
    ? Math.ceil(orderState.length / itemsPerPage)
    : 0;

  const cancelOrder = async (id) => {
    try {
      const resultAction = await dispatch(
        updateAnOrder({ status: "Cancelled", id })
      );

      if (updateAnOrder.fulfilled.match(resultAction)) {
        setApiModal({
          show: true,
          type: "success",
          title: "Order Cancelled",
          message: "Your order has been cancelled successfully",
        });

        dispatch(getOrders(config));
      }

      if (updateAnOrder.rejected.match(resultAction)) {
        setApiModal({
          show: true,
          type: "error",
          title: "Cancellation Failed",
          message:
            resultAction.error?.message ||
            "Unable to cancel the order. Please try again.",
        });
      }
    } catch (err) {
      setApiModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Something went wrong. Please try again.",
      });
    }
  };

  const submitReturnRequest = async () => {
    if (!returnReason.trim()) {
      setApiModal({
        show: true,
        type: "error",
        title: "Reason Required",
        message: "Please enter reason for return.",
      });
      return;
    }

    try {
      const resultAction = await dispatch(
        updateAnOrder({
          status: "Return-Requested",
          reason: returnReason,
          id: selectedOrderId,
        })
      );

      if (updateAnOrder.fulfilled.match(resultAction)) {
        setApiModal({
          show: true,
          type: "success",
          title: "Return Requested",
          message:
            "We’ve received your return request. Pickup will be scheduled soon.",
        });
        dispatch(getOrders(config));
      }

      if (updateAnOrder.rejected.match(resultAction)) {
        setApiModal({
          show: true,
          type: "error",
          title: "Return Failed",
          message:
            resultAction.error?.message ||
            "Unable to request return. Please try again.",
        });
      }
    } catch (err) {
      setApiModal({
        show: true,
        type: "error",
        title: "Error",
        message: "Something went wrong. Please try again.",
      });
    }

    setShowReturnModal(false);
    setReturnReason("");
  };

  return (
    <>
      <ApiResponseModal
        show={apiModal.show}
        type={apiModal.type}
        title={apiModal.title}
        message={apiModal.message}
        onClose={() => setApiModal((prev) => ({ ...prev, show: false }))}
      />

      <BreadCrumb title="My Orders" />

      <style>{`
        .order-card-mobile {
          background: #fff;
          border-radius: 10px;
          padding: 10px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.06);
          font-size: 13px;
        }
        .order-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }
        .order-id {
          font-size: 12px;
          font-weight: 600;
          color: #555;
        }
        .order-card-prices {
          display: flex;
          justify-content: space-between;
          font-weight: 600;
          margin-bottom: 6px;
        }
        .order-products {
          border-top: 1px dashed #ddd;
          padding-top: 6px;
          margin-top: 6px;
        }
        .order-product-row {
          display: flex;
          gap: 8px;
          align-items: center;
          margin-bottom: 6px;
        }
        .order-product-img {
          width: 42px;
          height: 42px;
          border-radius: 6px;
          object-fit: cover;
        }
        .order-product-info {
          flex: 1;
          overflow: hidden;
        }
        .order-product-title {
          font-size: 12px;
          font-weight: 500;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .order-product-meta {
          font-size: 11px;
          color: #666;
        }
        .order-actions {
          margin-top: 6px;
        }
        .order-actions .btn {
          font-size: 12px;
          padding: 4px;
        }
      `}</style>

      {paginatedOrders?.length > 0 ? (
        <Container class1="cart-wrapper home-wrapper-2 py-4">
          {/* DESKTOP */}
          <div className="d-none d-md-block">
            <div className="table-responsive">
              <table className="table table-bordered align-middle">
                <thead className="table-dark text-center">
                  <tr>
                    <th>Order ID</th>
                    <th>Total</th>
                    <th>After Discount</th>
                    <th>Status</th>
                    <th>Products</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedOrders.map((order, index) => (
                    <tr key={index}>
                      <td style={{ wordBreak: "break-all" }}>{order?._id}</td>
                      <td className="text-center">₹{order?.totalPrice}</td>
                      <td className="text-center text-success">
                        ₹{order?.totalPriceAfterDiscount}
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge ${
                            order?.orderStatus === "Cancelled"
                              ? "bg-danger"
                              : "bg-success"
                          }`}
                        >
                          {order?.orderStatus}
                        </span>
                      </td>
                      <td>
                        {order?.orderItems?.map((item, idx) => (
                          <div key={idx} className="d-flex gap-2 mb-2">
                            <img
                              src={
                                item?.product?.images?.[0]?.url ||
                                "/images/noimage.png"
                              }
                              alt="product"
                              width="45"
                              height="45"
                              style={{ objectFit: "cover", borderRadius: 6 }}
                              onClick={() => {
                                navigate("/product/" + item?.product?._id);
                              }}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                const fallback =
                                  item?.product?.images?.[1]?.url ||
                                  "https://via.placeholder.com/600x600?text=No+Image";
                                if (e.currentTarget.src !== fallback) {
                                  e.currentTarget.src = fallback;
                                }
                              }}
                            />
                            <div>
                              <strong>{item?.product?.title}</strong>
                              <div className="small text-muted">
                                Qty: {item?.quantity} | ₹{item?.price}
                              </div>
                            </div>
                          </div>
                        ))}
                      </td>

                      <td className="text-center">
                        {order?.orderStatus === "Cancelled" ? (
                          <span className="text-danger">Cancelled</span>
                        ) : order?.orderStatus === "Delivered" ? (
                          <button
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => {
                              setSelectedOrderId(order?._id);
                              setShowReturnModal(true);
                            }}
                          >
                            Return
                          </button>
                        ) : order?.shipUrl &&
                          order?.orderStatus !== "Ordered" ? (
                          <a
                            href={order?.shipUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-sm btn-outline-primary"
                          >
                            Track
                          </a>
                        ) : (
                          <button
                            onClick={() => cancelOrder(order?._id)}
                            className="btn btn-sm btn-outline-danger"
                          >
                            Cancel
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* MOBILE */}
          <div className="d-md-none">
            <div className="row g-2">
              {paginatedOrders.map((order, index) => (
                <div key={index} className="col-12">
                  <div className="order-card-mobile">
                    <div className="order-card-header">
                      <span className="order-id">
                        #{order?._id?.slice(-8)}
                      </span>
                      <span
                        className={`badge ${
                          order?.orderStatus === "Cancelled"
                            ? "bg-danger"
                            : "bg-success"
                        }`}
                      >
                        {order?.orderStatus}
                      </span>
                    </div>

                    <div className="order-card-prices">
                      <span>₹{order?.totalPrice}</span>
                      <span className="text-success">
                        ₹{order?.totalPriceAfterDiscount}
                      </span>
                    </div>

                    <div className="order-products">
                      {order?.orderItems?.map((item, idx) => (
                        <div
                          key={idx}
                          className="order-product-row"
                          onClick={() =>
                            navigate("/product/" + item?.product?._id)
                          }
                        >
                          <img
                            src={
                              item?.product?.images?.[0]?.url ||
                              "/images/noimage.png"
                            }
                            alt="product"
                            className="order-product-img"
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              const fallback =
                                item?.product?.images?.[1]?.url ||
                                "https://via.placeholder.com/600x600?text=No+Image";
                              if (e.currentTarget.src !== fallback) {
                                e.currentTarget.src = fallback;
                              }
                            }}
                          />
                          <div className="order-product-info">
                            <div className="order-product-title">
                              {item?.product?.title}
                            </div>
                            <div className="order-product-meta">
                              x{item?.quantity} • ₹{item?.price}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="order-actions">
                      {order?.orderStatus === "Cancelled" ? (
                        <span className="text-danger small">Cancelled</span>
                      ) : order?.orderStatus === "Delivered" ? (
                        <button
                          onClick={() => {
                            setSelectedOrderId(order?._id);
                            setShowReturnModal(true);
                          }}
                          className="btn btn-outline-warning btn-sm w-100"
                        >
                          Return Order
                        </button>
                      ) : order?.shipUrl &&
                        order?.orderStatus !== "Ordered" ? (
                        <a
                          href={order?.shipUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="btn btn-outline-primary btn-sm w-100"
                        >
                          Track Order
                        </a>
                      ) : (
                        <button
                          onClick={() => cancelOrder(order?._id)}
                          className="btn btn-outline-danger btn-sm w-100"
                        >
                          Cancel Order
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <ul className="pagination pagination-sm">
                {[...Array(totalPages)].map((_, i) => (
                  <li
                    key={i}
                    className={`page-item ${
                      currentPage === i + 1 ? "active" : ""
                    }`}
                  >
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </Container>
      ) : (
        <DataNotFound />
      )}

      {/* RETURN MODAL */}
      {showReturnModal && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h6 className="modal-title">Return Order</h6>
                <button
                  className="btn-close"
                  onClick={() => {
                    setShowReturnModal(false);
                    setReturnReason("");
                  }}
                />
              </div>
              <div className="modal-body">
                <label className="form-label">Reason *</label>
                <textarea
                  className="form-control"
                  rows="3"
                  value={returnReason}
                  placeholder="Why do you want to return the order?"
                  onChange={(e) => setReturnReason(e.target.value)}
                ></textarea>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowReturnModal(false);
                    setReturnReason("");
                  }}
                >
                  Close
                </button>
                <button className="btn btn-warning" onClick={submitReturnRequest}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Orders;
